export type DirectiveScope =
  | 'global'
  | 'engine-mcp'
  | 'crm'
  | 'newsletter'
  | 'tasks'
  | 'thoughts';

export type DirectiveStatus = 'active' | 'superseded' | 'revoked';

export interface AgentDirective {
  id: string;
  key: string;
  scope: DirectiveScope;
  directive: string;
  rationale: string | null;
  status: DirectiveStatus;
  priority: number;
  applies_to: string[];
  examples: Record<string, unknown>;
  metadata: Record<string, unknown>;
  superseded_by: string | null;
  created_via: string;
  created_at: string;
  updated_at: string;
}

export interface DirectiveRevision {
  id: string;
  directive_id: string;
  event: 'created' | 'updated' | 'superseded' | 'revoked';
  previous_text: string | null;
  new_text: string | null;
  previous_status: string | null;
  new_status: string | null;
  note: string | null;
  source: string;
  created_at: string;
}

export interface ContextBrief {
  id: string;
  slug: string;
  title: string;
  body: string;
  status: 'active' | 'archived';
  priority: number;
  expires_at: string | null;
  created_via: string;
  created_at: string;
  updated_at: string;
}

export const DIRECTIVE_SCOPES: DirectiveScope[] = [
  'global',
  'engine-mcp',
  'crm',
  'newsletter',
  'tasks',
  'thoughts',
];

export const SCOPE_COLORS: Record<DirectiveScope, string> = {
  global: 'bg-violet-100 dark:bg-violet-900 text-violet-700 dark:text-violet-300',
  'engine-mcp': 'bg-sky-100 dark:bg-sky-900 text-sky-700 dark:text-sky-300',
  crm: 'bg-emerald-100 dark:bg-emerald-900 text-emerald-700 dark:text-emerald-300',
  newsletter: 'bg-amber-100 dark:bg-amber-900 text-amber-700 dark:text-amber-300',
  tasks: 'bg-orange-100 dark:bg-orange-900 text-orange-700 dark:text-orange-300',
  thoughts: 'bg-purple-100 dark:bg-purple-900 text-purple-700 dark:text-purple-300',
};

export type WorkflowStatus = 'draft' | 'active' | 'superseded' | 'retired';

export type WorkflowStep = {
  tool: string;
  purpose?: string;
  confirm_before?: boolean;
  args?: Record<string, unknown>;
  note?: string;
};

export interface AgentWorkflow {
  id: string;
  slug: string;
  title: string;
  triggers: string[];
  steps: WorkflowStep[];
  success_shape: string | null;
  anti_patterns: string[];
  status: WorkflowStatus;
  priority: number;
  version: number;
  superseded_by: string | null;
  created_via: string;
  created_at: string;
  updated_at: string;
}

export interface WorkflowRevision {
  id: string;
  workflow_id: string;
  event: 'created' | 'updated' | 'superseded' | 'retired' | 'activated';
  previous_steps: WorkflowStep[] | null;
  new_steps: WorkflowStep[] | null;
  previous_status: string | null;
  new_status: string | null;
  note: string | null;
  source: string;
  created_at: string;
}

export const WORKFLOW_STATUS_BADGE: Record<WorkflowStatus, string> = {
  draft: 'bg-amber-100 dark:bg-amber-900 text-amber-700 dark:text-amber-300',
  active: 'bg-emerald-100 dark:bg-emerald-900 text-emerald-700 dark:text-emerald-300',
  superseded: 'bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400',
  retired: 'bg-red-100 dark:bg-red-900 text-red-600 dark:text-red-300',
};
